// biome-ignore-all lint/suspicious/noArrayIndexKey: TODO - address later
import { Megaphone } from "lucide-react";
import { Link, useNavigate } from "react-router";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useHighRecentEngagementCampaigns } from "@/hooks/api/use-high-recent-engagement-campaigns";
import { cn } from "@/lib/utils";

interface HighRecentEngagementCampaignsProps {
  days?: number;
  limit?: number;
  className?: string;
}

export function HighRecentEngagementCampaigns({
  days = 30,
  limit = 5,
  className,
}: HighRecentEngagementCampaignsProps) {
  const navigate = useNavigate();
  const {
    data: campaigns,
    isLoading,
    error,
  } = useHighRecentEngagementCampaigns(days, limit);

  const handleRowClick = (event: React.MouseEvent, campaignId: string) => {
    const url = `/campaigns/${campaignId}/explorer`;
    if (event.metaKey || event.ctrlKey) {
      window.open(url, "_blank");
    } else {
      navigate(url);
    }
  };

  return (
    <Card
      className={cn("w-full", className)}
      data-testid="high-engagement-campaigns-card"
    >
      <CardHeader className="flex flex-row items-center gap-x-3 pb-2">
        <Megaphone className="h-9 w-9 text-primary flex-shrink-0" />
        <div className="space-y-1">
          <CardTitle className="text-md">
            <span>High-engagement campaigns</span>
          </CardTitle>
          <CardDescription>
            Selection of campaigns with high engagement in the last 30 days
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent className="pt-4">
        {isLoading ? (
          <div className="space-y-3">
            {Array(3)
              .fill(0)
              .map((_, index) => (
                <div key={index} className="flex justify-between">
                  <Skeleton className="h-5 w-[200px]" />
                  <Skeleton className="h-5 w-[100px]" />
                </div>
              ))}
          </div>
        ) : error ? (
          <div className="text-sm text-muted-foreground py-2">
            Error loading high engagement campaigns.
          </div>
        ) : !campaigns?.length ? (
          <div className="text-sm text-muted-foreground py-2">
            No high engagement campaigns found.
          </div>
        ) : (
          <div className="space-y-1">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-3/4">Campaign</TableHead>
                  <TableHead className="text-right whitespace-nowrap w-1/4">
                    Recent Engagements
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {campaigns.map((campaign) => (
                  <Tooltip key={campaign.campaign_id}>
                    <TooltipTrigger asChild>
                      <TableRow
                        className="cursor-pointer hover:bg-accent group"
                        onClick={(e) => handleRowClick(e, campaign.campaign_id)}
                        data-testid={`campaign-row-${campaign.campaign_id}`}
                      >
                        <TableCell>
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="font-medium">
                                {campaign.campaign_name}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {campaign.campaign_type || "Campaign"}
                              </div>
                            </div>
                            <Link
                              to={`/campaigns/${campaign.campaign_id}/explorer`}
                              onClick={(e) => e.stopPropagation()}
                            ></Link>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          {campaign.total_recent_engagements.toLocaleString()}
                        </TableCell>
                      </TableRow>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>View campaign report card</p>
                    </TooltipContent>
                  </Tooltip>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
